import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      // Server truth wins over stale localStorage flags. If the active session
      // is NOT a demo session, scrub any leftover demo-mode markers so admin
      // pages render with full operator controls.
      if (data && data.is_demo === false) {
        try {
          localStorage.removeItem("investorDemoMode");
          localStorage.removeItem("investorDemoVia");
          localStorage.removeItem("investorDemoAutoTour");
        } catch { /* localStorage unavailable */ }
      }
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    // Step-1 may return { requires_totp: true, ticket } — caller handles step-2.
    if (data?.requires_totp) return data;
    // Real admin login wipes any sticky demo-session flags from a prior visit.
    try {
      localStorage.removeItem("investorDemoMode");
      localStorage.removeItem("investorDemoVia");
      localStorage.removeItem("investorDemoAutoTour");
    } catch { /* localStorage unavailable — safe to ignore */ }
    setUser(data);
    return data;
  };

  const verify2fa = async (ticket, code) => {
    const { data } = await axios.post(`${API}/auth/login/2fa`, { ticket, code }, { withCredentials: true });
    try {
      localStorage.removeItem("investorDemoMode");
      localStorage.removeItem("investorDemoVia");
      localStorage.removeItem("investorDemoAutoTour");
    } catch { /* localStorage unavailable */ }
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, verify2fa }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
